// Cart accessory upsell carousel — turns the multi-card upsell
// rail in `snippets/cart-upsell.liquid` into a horizontal scroll-snap
// carousel with prev/next arrow controls. Native swipe handles mobile;
// arrows handle pointer/keyboard users on the wider drawer + cart page
// layouts. Boundary state (prev disabled at scrollLeft 0, next disabled at
// scrollWidth - clientWidth) is recomputed on scroll + resize + cart
// morph + any external `[disabled]` mutation, so the rail re-evaluates
// whenever cards are added, removed, re-rendered, or the section is
// hydrated under us by another part of the theme.
//
// The element is *inside* the section, but the prev/next buttons live in
// the section header above it (so the arrows can sit beside the heading).
// `closest('.cart-upsell')` bridges between them — keeps the markup
// readable without forcing the arrows down into the rail. We also use a
// section-level click delegation for the buttons so the listener
// survives any morph the cart engine throws at the section subtree.
//
// Why the cart morph hook matters: this element extends `HTMLElement`
// directly (not the theme's `Component`), so when `assets/morph.js`
// re-renders the cart section after add-to-cart / remove / quantity
// change, it does NOT call any `updatedCallback` on us. Without the
// `cart:update` listener, the nav was getting stuck in its pre-morph
// state — e.g. user removes one item, candidate count grows from 1 → 2,
// the rail now overflows again, but the arrows never reappeared because
// nothing told us to remeasure. Listening for `cart:update` on
// `document` re-runs `#update()` once the morph has settled (we defer
// to the next rAF so the morph's synchronous DOM edits land first).

import { ThemeEvents } from "@theme/events";

class CartUpsellCarousel extends HTMLElement {
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {MutationObserver | null} */
  #attrObserver = null;

  /** @type {number | null} */
  #rafId = null;

  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    if (!this.#getRail()) return;

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    // Capture-phase scroll listener on the host (rather than directly on
    // the rail). Scroll events don't bubble, but they DO traverse the
    // capture phase — so an ancestor listener fires when a descendant
    // scrolls. We bind on the host because the rail `<ul>` can be
    // swapped out by morph (cart drawer hydration runs a full morph
    // inside `[data-hydration-key="cart-drawer-inner"]`, and cart-update
    // morph walks the section subtree); the host element survives those
    // morphs, so the listener stays connected across re-renders.
    // The rail is the only scrollable descendant, so we don't need to
    // filter `event.target` — `#update()` is cheap and idempotent.
    this.addEventListener("scroll", this.#handleScroll, { capture: true, passive: true, signal });

    const section = this.closest(".cart-upsell");
    section?.addEventListener("click", this.#handleNavClick, { signal });

    // Observe the host (preserved across morph) instead of the rail
    // (swappable by morph). Size changes that affect overflow detection
    // — viewport resizes, drawer-open layout shifts, sibling content
    // growing/shrinking — reach the host first since the rail's outer
    // box tracks the host's content area.
    this.#resizeObserver = new ResizeObserver(() => this.#update());
    this.#resizeObserver.observe(this);

    // The rail can be inside a `<dialog>` that's still closed when this
    // element first connects (cart drawer); in that case `clientWidth` /
    // `scrollWidth` both read 0 and `update()` would lock state into
    // a no-overflow snapshot. Deferring to the next rAF means the
    // dialog's first paint (and the rail's first real layout) lands
    // first. The ResizeObserver also catches it when the dialog later
    // opens, but belt-and-suspenders here removes the visible flash.
    this.#scheduleUpdate();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, { signal });

    // Self-healing observer: re-apply our intended `[disabled]` state on
    // the prev/next buttons whenever something else mutates them.
    //
    // Why this exists: the parent header section runs deferred hydration
    // via `assets/section-hydration.js` (kicked off by the inline script
    // at the bottom of `sections/header.liquid` on `requestIdleCallback`).
    // That hydration morphs fresh server-rendered HTML into the live
    // header on the FIRST page load only. The new HTML doesn't have
    // `disabled` on our prev/next buttons (Liquid never renders that —
    // we set it at runtime), so morph's `copyAttributes` reconciles by
    // *removing* the `disabled` attribute we'd set milliseconds earlier
    // — re-enabling the prev arrow even though the user is visibly
    // still on the first card. The reason a close-then-reopen of the
    // drawer "fixed" it permanently was that section hydration sets
    // `section.dataset.hydrated = "true"` after the one run and bails
    // out on subsequent calls — so the bug was genuinely once per page
    // lifecycle. This MutationObserver makes us robust against not
    // just that one trigger but anything in the future that morphs the
    // section subtree (variant pickers, theme editor reloads, etc.).
    //
    // Filter to mutations on the prev/next buttons themselves: the
    // subtree includes every add-to-cart-button inside the cards, and
    // those toggle `[disabled]` constantly during the buy flow — we'd
    // be scheduling an update on every click without this guard.
    //
    // Loop concern: when `#update` calls `toggleAttribute("disabled", X)`,
    // the observer fires. But `#scheduleUpdate` is rAF-coalesced and the
    // re-run sees the state already matches the desired value, so its
    // `toggleAttribute` is a no-op (no further mutations). Net cost:
    // exactly one extra rAF per real attribute change.
    if (section) {
      this.#attrObserver = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          const target = mutation.target;
          if (target instanceof Element && target.matches("[data-cart-upsell-prev], [data-cart-upsell-next]")) {
            this.#scheduleUpdate();
            return;
          }
        }
      });
      this.#attrObserver.observe(section, {
        attributes: true,
        attributeFilter: ["disabled"],
        subtree: true,
      });
    }
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#resizeObserver?.disconnect();
    this.#attrObserver?.disconnect();
    if (this.#rafId !== null) {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = null;
    }
  }

  /**
   * Always query fresh — the `<ul>` can be swapped by morph (cart drawer
   * hydration / cart-update morph rebuild the rail when Liquid's
   * `candidate_handles.size` count changes between renders), so any
   * cached reference can point at a detached node. Cheap: single
   * attribute selector against a small subtree.
   * @returns {HTMLElement | null}
   */
  #getRail() {
    return this.querySelector("[data-cart-upsell-rail]");
  }

  /**
   * Section-level click delegation for prev/next. Survives a nav node
   * being removed and re-inserted by morph (since the listener lives on
   * the section, which is itself preserved across the morph).
   */
  #handleNavClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (target.closest("[data-cart-upsell-prev]")) {
      this.#scrollPrev();
    } else if (target.closest("[data-cart-upsell-next]")) {
      this.#scrollNext();
    }
  };

  /**
   * Single rAF coalescer for every `#update()` trigger: scroll events,
   * cart morphs, attribute mutations, dialog opens. Multiple calls in
   * the same frame collapse into one update on the next frame. Deferring
   * also gives layout a chance to settle (e.g. after a cart morph, so
   * we measure the post-morph rail rather than the pre-morph one).
   */
  #scheduleUpdate = () => {
    if (this.#rafId !== null) return;
    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = null;
      this.#update();
    });
  };

  /**
   * Cart morphs (after add-to-cart / remove / quantity change) re-render
   * the section's HTML in place. Because this element doesn't extend
   * `Component`, `morph.js` doesn't call any post-update hook on us —
   * we have to listen on `document` ourselves. Schedule an update for
   * the next frame so the morph's synchronous DOM edits land first
   * (otherwise we'd measure the pre-morph rail).
   */
  #handleCartUpdate = () => this.#scheduleUpdate();

  /**
   * Width to scroll on each arrow click — one card width + the inter-card
   * gap, so the next snap-aligned card lands flush at the start edge.
   */
  #getStep() {
    const rail = this.#getRail();
    if (!rail) return 0;
    const card = rail.querySelector(".cart-upsell__card-wrapper");
    // Liquid only renders the carousel when there's at least one candidate;
    // this guard is purely defensive against a mid-morph state.
    if (!card) return 0;
    const styles = getComputedStyle(rail);
    const gap = parseFloat(styles.columnGap) || parseFloat(styles.gap) || 0;
    return card.getBoundingClientRect().width + gap;
  }

  #scrollPrev = () => {
    this.#getRail()?.scrollBy({ left: -this.#getStep(), behavior: "smooth" });
  };

  #scrollNext = () => {
    this.#getRail()?.scrollBy({ left: this.#getStep(), behavior: "smooth" });
  };

  #handleScroll = () => this.#scheduleUpdate();

  #update = () => {
    // Always query fresh — see `#getRail()`. The cart drawer morph
    // (hydration mode rooted at `[data-hydration-key="cart-drawer-inner"]`)
    // can swap out the rail `<ul>`, the whole `<div class="cart-upsell__nav">`,
    // or both whenever Liquid's `candidate_handles.size > 1` gate flips.
    // After a 2 → 1 → 2 cycle, any cached refs would point at the
    // *original* (now detached) nodes, and we'd be flipping `[disabled]`
    // on nodes that aren't on the page anymore.
    const rail = this.#getRail();
    if (!rail) return;

    const section = this.closest(".cart-upsell");
    const prevButton = section?.querySelector("[data-cart-upsell-prev]");
    const nextButton = section?.querySelector("[data-cart-upsell-next]");

    const overflows = rail.scrollWidth - rail.clientWidth > 1;

    // Geometry-based boundary detection. We deliberately do NOT compute
    // `atStart` / `atEnd` from `scrollLeft` alone — an earlier version
    // did (`scrollLeft <= scrollPaddingInlineStart + 1`) and the bug it
    // kept producing was: on a fresh page reload's first cart drawer
    // open, a late layout reflow (`scroll-hint` mask install,
    // sticky-state attribute flip, image/font hydration, etc.) would
    // re-trigger the scroll-snap engine and land `scrollLeft` anywhere
    // from a few px to ~half a card width above the start, even though
    // the first card was visibly still the snapped card. Comparing
    // bounding rects answers the question we actually mean: "is the
    // first/last card still the one sitting at the leading/trailing
    // edge of the rail's visible area?". Using the rail's center as
    // the threshold means the answer is yes whenever more than half of
    // the relevant card is on screen — which covers any wobble the
    // snap engine can produce.
    const cards = rail.querySelectorAll(".cart-upsell__card-wrapper");
    const firstCard = cards[0];
    const lastCard = cards[cards.length - 1];

    let atStart = true;
    let atEnd = true;
    if (firstCard && lastCard) {
      const railRect = rail.getBoundingClientRect();
      const railCenter = railRect.left + railRect.width / 2;

      atStart = firstCard.getBoundingClientRect().right > railCenter;
      atEnd = lastCard.getBoundingClientRect().left < railCenter;
    }

    // Boundary-state on the buttons. When the rail doesn't overflow,
    // both ends are simultaneously "reached", so both buttons end up
    // `[disabled]` — and the CSS in `cart-upsell.liquid` collapses the
    // whole nav via `:not(:has(button:not([disabled])))`. That way the
    // hide-when-no-overflow logic is purely declarative and not
    // affected by any transient ResizeObserver tick that briefly
    // measures `scrollWidth === clientWidth` mid-layout.
    if (prevButton) prevButton.toggleAttribute("disabled", !overflows || atStart);
    if (nextButton) nextButton.toggleAttribute("disabled", !overflows || atEnd);
  };
}

if (!customElements.get("cart-upsell-carousel")) {
  customElements.define("cart-upsell-carousel", CartUpsellCarousel);
}

// Inline colour swatches on accessory cards (`snippets/accessory-card.liquid`),
// wherever a card renders: the cart drawer, the cart page, or a product page's
// Accessory add block. A chip click syncs the card's add-to-cart form
// (`input[name="id"]` value + disabled state), the selected-name text, chip
// pressed states and the add button to the picked variant, so the form posts
// that exact variant through the normal single-line path.
//
// Delegated on `document` so it survives cart morphs (SSR re-paints the row's
// default state on every morph, which also resets selection after an add) and
// needs no wrapper element outside the cart's carousel.
/** @param {Event} event */
function handleAccessorySwatchClick(event) {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const swatch = target.closest("[data-upsell-swatch]");
  if (!(swatch instanceof HTMLButtonElement) || swatch.disabled) return;

  const card = swatch.closest("product-card");
  if (!card) return;

  const variantId = swatch.dataset.variantId || "";
  if (!variantId) return;

  for (const chip of card.querySelectorAll("[data-upsell-swatch]")) {
    const isSelected = chip === swatch;
    chip.classList.toggle("cart-upsell__swatch--selected", isSelected);
    chip.setAttribute("aria-pressed", String(isSelected));
  }

  // The name is a grid-stack of ALL value names (widest one sizes the box
  // so selection can't reflow the row) — toggle visibility, don't write
  // textContent: that would blow away the stacked spans and the stable
  // width with them.
  for (const option of card.querySelectorAll("[data-upsell-name-option]")) {
    option.classList.toggle("is-selected", option.dataset.upsellNameOption === variantId);
  }

  // Thumbnail follows the selected colour — ANY selection pins the
  // variant's image (including re-picking the default colour); the
  // lifestyle product photo only shows in the untouched default state.
  // srcset must be overwritten too: the SSR widths list would otherwise
  // outrank the new src. A variant without a featured image (empty
  // data attr) keeps whatever the card currently shows.
  const variantImage = swatch.dataset.variantImage;
  const img = card.querySelector("img.cart-upsell__image");
  if (variantImage && img instanceof HTMLImageElement) {
    img.src = variantImage;
    img.srcset = variantImage;
  }

  // The disabled-chip guard above means every selectable chip IS an
  // available variant, so selecting one always re-arms the form: the id
  // input may be SSR-disabled when the DEFAULT variant is sold out —
  // re-enable it (a disabled input is EXCLUDED from FormData, which would
  // otherwise submit an id-less add) — and the add button follows.
  const idInput = /** @type {HTMLInputElement | null} */ (card.querySelector('form input[name="id"]'));
  if (idInput) {
    idInput.value = variantId;
    idInput.disabled = false;
  }

  const addButton = /** @type {HTMLButtonElement | null} */ (card.querySelector("form .add-to-cart-button"));
  if (addButton) addButton.disabled = false;
}

document.addEventListener("click", handleAccessorySwatchClick);
